import React, { useState, useEffect } from 'react';
import './Sidebar.css';
import { exportKeysToFile } from '../utils/crypto';
import { authAPI } from '../utils/api';
import { getPrivateChats, getPendingPrivateChats } from '../utils/privateChatAPI';
import CreatePrivateChatModal from './CreatePrivateChatModal';
import UpgradeAccountModal from './UpgradeAccountModal';

const Sidebar = ({ currentChat, onChatSelect, onLogout, userKeys, currentView, onViewChange, onCreateNote, onPrivateChatSelect, privateChatRefreshTrigger }) => {
  const [chatList, setChatList] = useState([
    { id: 'public', name: 'ToxChat Room', type: 'public', unread: 0, online: true }
  ]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchUserId, setSearchUserId] = useState('');
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [showOnlineUsers, setShowOnlineUsers] = useState(false);
  const [onlineUserSearch, setOnlineUserSearch] = useState('');
  const [onlineUserPage, setOnlineUserPage] = useState(1);
  const [onlineUsersPerPage] = useState(10);
  
  // 私聊会话状态
  const [privateChats, setPrivateChats] = useState([]);
  const [pendingChats, setPendingChats] = useState([]);
  const [privateChatsLoading, setPrivateChatsLoading] = useState(false);
  
  // 创建私聊模态框状态
  const [showCreateChatModal, setShowCreateChatModal] = useState(false);
  const [targetUserID, setTargetUserID] = useState('');
  
  // 升级账户模态框状态
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // 获取真实的在线用户数据
  useEffect(() => {
    const fetchOnlineUsers = async () => {
      if (!userKeys) return;
      
      try {
        const response = await authAPI.getOnlineUsers(userKeys);
        if (response.success) {
          const users = (response.users || []).map(user => ({
            id: user.user_id,
            isOnline: user.is_online,
            lastSeen: new Date(user.last_seen),
            createdAt: new Date(user.created_at)
          }));
          setOnlineUsers(users);
        } else {
        }
      } catch (error) {
      }
    };

    fetchOnlineUsers();
    
    // 每30秒刷新一次在线用户列表
    const interval = setInterval(fetchOnlineUsers, 30000);
    
    return () => clearInterval(interval);
  }, [userKeys]);

  // 获取私聊会话数据
  useEffect(() => {
    const fetchPrivateChats = async () => {
      if (!userKeys) return;
      
      try {
        setPrivateChatsLoading(true);
        const [chatsData, pendingData] = await Promise.all([
          getPrivateChats(userKeys),
          getPendingPrivateChats(userKeys)
        ]);
        
        setPrivateChats(chatsData || []);
        setPendingChats(pendingData || []);
      } catch (error) {
        setPrivateChats([]);
        setPendingChats([]);
      } finally {
        setPrivateChatsLoading(false);
      }
    };

    fetchPrivateChats();
    
    // 每15秒刷新一次私聊会话列表（用于显示新消息角标）
    const interval = setInterval(fetchPrivateChats, 15000);
    
    return () => clearInterval(interval);
  }, [userKeys, privateChatRefreshTrigger]);

  // 模拟会话数据
  useEffect(() => {
    const generateMockConversations = () => {
      const conversations = [
        { id: 'public', name: 'ToxChat Room', type: 'public', unread: 0, online: true }
      ];
      
      // 生成50个模拟私聊会话
      for (let i = 1; i <= 50; i++) {
        const userId = `user${i.toString().padStart(3, '0')}${Math.random().toString(36).substr(2, 6)}`;
        conversations.push({
          id: userId,
          name: userId,
          type: 'private',
          unread: Math.floor(Math.random() * 5), // 0-4条未读消息
          online: Math.random() > 0.4, // 60% online rate
          lastMessage: `Last message ${i}`,
          lastMessageTime: new Date(Date.now() - Math.random() * 86400000) // Random time within last day
        });
      }
      
      // 按最后消息时间排序
      return conversations.sort((a, b) => {
        if (a.type === 'public') return -1; // 公共聊天室始终在顶部
        return (b.lastMessageTime || 0) - (a.lastMessageTime || 0);
      });
    };
    
    setChatList(generateMockConversations());
  }, []);

  const handleExportKeys = () => {
    if (userKeys) {
      exportKeysToFile(userKeys);
    }
    setShowUserMenu(false);
  };

  const handleUpgradeAccount = () => {
    setShowUpgradeModal(true);
    setShowUserMenu(false);
  };

  const handleStartChat = (userId) => {
    // 检查是否已经存在私聊会话
    const existingChat = privateChats.find(chat => chat.otherUserID === userId);
    if (existingChat) {
      // 如果已存在，直接打开私聊对话框
      if (onPrivateChatSelect) {
        onPrivateChatSelect(existingChat);
      }
      return;
    }

    // 如果不存在，显示创建私聊模态框
    setTargetUserID(userId);
    setShowCreateChatModal(true);
    setSearchUserId('');
  };

  const handleCreateChat = async (firstMessage) => {
    try {
      // 创建私聊会话
      if (userKeys && userKeys.publicKey) {
        const { createPrivateChat } = await import('../utils/privateChatAPI');
        const result = await createPrivateChat(
          targetUserID, 
          firstMessage, 
          userKeys.publicKey, 
          userKeys
        );
        
        if (result && result.chat) {
          // 创建成功后，刷新私聊会话列表
          const [chatsData, pendingData] = await Promise.all([
            getPrivateChats(userKeys),
            getPendingPrivateChats(userKeys)
          ]);
          setPrivateChats(chatsData || []);
          setPendingChats(pendingData || []);
          
          // 打开私聊对话框
          if (onPrivateChatSelect) {
            onPrivateChatSelect(result.chat);
          }
        }
      } else {
        throw new Error('User keys not available. Please log in again.');
      }
    } catch (error) {
      throw error;
    }
  };


  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchUserId.trim()) {
      handleStartChat(searchUserId.trim());
    }
  };

  // 过滤和分页在线用户
  const filteredOnlineUsers = onlineUsers.filter(user => 
    user.isOnline && user.id.toLowerCase().includes(onlineUserSearch.toLowerCase())
  );
  
  const totalPages = Math.ceil(filteredOnlineUsers.length / onlineUsersPerPage);
  const paginatedUsers = filteredOnlineUsers.slice(
    (onlineUserPage - 1) * onlineUsersPerPage,
    onlineUserPage * onlineUsersPerPage
  );

  const handleOnlineUserSearch = (e) => {
    setOnlineUserSearch(e.target.value);
    setOnlineUserPage(1); // Reset to first page when searching
  };

  const handlePageChange = (newPage) => {
    setOnlineUserPage(newPage);
  };

  const handleRefreshOnlineUsers = async () => {
    if (!userKeys) return;
    
    try {
      const response = await authAPI.getOnlineUsers(userKeys);
      if (response.success) {
        const users = response.users.map(user => ({
          id: user.user_id,
          isOnline: user.is_online,
          lastSeen: new Date(user.last_seen),
          createdAt: new Date(user.created_at)
        }));
        setOnlineUsers(users);
      } else {
      }
    } catch (error) {
    }
  };

  // 处理私聊会话点击
  const handlePrivateChatClick = (chat) => {
    if (onPrivateChatSelect) {
      onPrivateChatSelect(chat);
    }
    // 确保切换到私聊模式
    if (onChatSelect) {
      onChatSelect('private');
    }
  };

  const handleDeleteConversation = (chatId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this conversation? This will permanently delete all messages and cannot be recovered.')) {
      setChatList(prev => prev.filter(chat => chat.id !== chatId));
      if (currentChat === chatId) {
        onChatSelect('public'); // Switch to public chat if deleting current chat
      }
    }
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <div className="user-info">
          <div className="online-indicator"></div>
          <span className="user-id">{userKeys?.userId}</span>
          <button 
            className="user-menu-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            ⚙
          </button>
        </div>
        
        {showUserMenu && (
          <div className="user-menu">
            <button onClick={handleUpgradeAccount}>Upgrade Account</button>
            <button onClick={handleExportKeys}>Export Keys</button>
            <button onClick={onLogout}>Logout</button>
          </div>
        )}
      </div>

      <div className="search-section">
        <form onSubmit={handleSearchSubmit}>
          <input
            type="text"
            placeholder="Search User ID..."
            value={searchUserId}
            onChange={(e) => setSearchUserId(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-btn">Search</button>
        </form>
      </div>

      <div className="main-menu">
        <div 
          className={`menu-item ${currentView === 'chat' ? 'active' : ''}`} 
          onClick={() => onViewChange('chat')}
        >
          <span>Chat</span>
        </div>
        <div 
          className={`menu-item ${currentView === 'notes' ? 'active' : ''}`} 
          onClick={() => onViewChange('notes')}
        >
          <span>Notes</span>
        </div>
      </div>

      {currentView === 'chat' ? (
        <div className="chat-list">
          {/* 公共聊天室区域 */}
          <div className="public-chat-section">
            <div className="section-title">Public Chat</div>
            <div className="public-chat-item">
              <div
                className={`chat-item ${currentChat === 'public' ? 'active' : ''}`}
                onClick={() => {
                  onChatSelect('public');
                  // 关闭当前私聊对话框
                  if (onPrivateChatSelect) {
                    onPrivateChatSelect(null);
                  }
                }}
              >
                <div className="chat-info">
                  <div className="online-indicator small"></div>
                  <span className="chat-name">ToxChat Room</span>
                  <span className="chat-type">[Public]</span>
                </div>
              </div>
            </div>
          </div>

          {/* 私聊会话区域 */}
          <div className="private-chats-section">
            <div className="section-title">
              Private Chats ({privateChats.length})
              {pendingChats.length > 0 && (
                <span className="pending-indicator"> ({pendingChats.length} pending)</span>
              )}
            </div>
            
            <div className="conversation-list">
              {privateChatsLoading ? (
                <div className="loading-conversations">
                  <div className="loading-spinner"></div>
                  <span>Loading private chats...</span>
                </div>
              ) : (
                <>
                  {privateChats.map(chat => (
                    <div
                      key={chat.chatID}
                      className="chat-item private-chat-item"
                      onClick={() => handlePrivateChatClick(chat)}
                    >
                      <div className="chat-info">
                        <div className="online-indicator small"></div>
                        <span className="private-lock-icon">🔒</span>
                        <span className="chat-name">{chat.otherUserID}</span>
                        {chat.status === 'pending' && chat.initiatorID && (
                          <span className="initiator-info">from {chat.initiatorID}</span>
                        )}
                        <span className="chat-type">[Private]</span>
                        {chat.unreadCount > 0 && (
                          <span className="unread-badge">{chat.unreadCount}</span>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {privateChats.length === 0 && !privateChatsLoading && (
                    <div className="no-conversations">
                      No private chats yet
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="notes-section">
          <div className="notes-header">
            <div className="section-title">Notes</div>
            <button className="create-note-btn" onClick={onCreateNote}>
              + New Note
            </button>
          </div>
           <div className="notes-info">
             <div className="info-item">
               <span className="info-number">1.</span>
               <div className="info-content">
                 <div className="info-title">End-to-End Encryption</div>
                 <div className="info-subtitle">Only you can read your notes.</div>
               </div>
             </div>
             <div className="info-item">
               <span className="info-number">2.</span>
               <div className="info-content">
                 <div className="info-title">Zero-Knowledge Storage</div>
                 <div className="info-subtitle">Server knows nothing inside.</div>
               </div>
             </div>
             <div className="info-item">
               <span className="info-number">3.</span>
               <div className="info-content">
                 <div className="info-title">Real-Time Sync</div>
                 <div className="info-subtitle">Fast & secure on any device.</div>
               </div>
             </div>
             <div className="info-item">
               <span className="info-number">4.</span>
               <div className="info-content">
                 <div className="info-title">Anonymous Access</div>
                 <div className="info-subtitle">No registration required.</div>
               </div>
             </div>
             <div className="info-item">
               <span className="info-number">5.</span>
               <div className="info-content">
                 <div className="info-title">Reliable Performance</div>
                 <div className="info-subtitle">Enjoy stability anytime.</div>
               </div>
             </div>
           </div>
        </div>
      )}

      <div className="sidebar-footer">
        <div className="online-users">
          <div className="online-users-header">
            <div className="section-title">
              Online Users ({filteredOnlineUsers.length})
              <div className="header-buttons">
                <button 
                  className="refresh-btn"
                  onClick={handleRefreshOnlineUsers}
                  title="Refresh Online Users"
                >
                  ↻
                </button>
                <button 
                  className="toggle-btn"
                  onClick={() => setShowOnlineUsers(!showOnlineUsers)}
                  title={showOnlineUsers ? 'Collapse' : 'Expand'}
                >
                  {showOnlineUsers ? '▼' : '▶'}
                </button>
              </div>
            </div>
          </div>
          
          {showOnlineUsers && (
            <>
              <div className="online-search">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={onlineUserSearch}
                  onChange={handleOnlineUserSearch}
                  className="online-search-input"
                />
              </div>
              
              <div className="online-list">
                {paginatedUsers.map(user => (
                  <div
                    key={user.id}
                    className="online-user"
                    onClick={() => handleStartChat(user.id)}
                  >
                    <div className="online-indicator small"></div>
                    <span className="private-lock-icon small">🔒</span>
                    <span className="user-id">{user.id}</span>
                  </div>
                ))}
                
                {paginatedUsers.length === 0 && (
                  <div className="no-users">
                    {onlineUserSearch ? 'No users found' : 'No users online'}
                  </div>
                )}
              </div>
              
              {totalPages > 1 && (
                <div className="online-pagination">
                  <button 
                    className="page-btn"
                    onClick={() => handlePageChange(onlineUserPage - 1)}
                    disabled={onlineUserPage === 1}
                  >
                    ◀
                  </button>
                  <span className="page-info">
                    {onlineUserPage} / {totalPages}
                  </span>
                  <button 
                    className="page-btn"
                    onClick={() => handlePageChange(onlineUserPage + 1)}
                    disabled={onlineUserPage === totalPages}
                  >
                    ▶
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="app-info">
          ToxChat v1.2.3
        </div>
      </div>
      
      {/* 创建私聊模态框 */}
      <CreatePrivateChatModal
        isOpen={showCreateChatModal}
        onClose={() => setShowCreateChatModal(false)}
        targetUserID={targetUserID}
        onCreateChat={handleCreateChat}
      />
      
      {/* 升级账户模态框 */}
      <UpgradeAccountModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
};

export default Sidebar;
